import { CSSResult, html, LitElement, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { repeat } from 'lit/directives/repeat.js';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import styles from './wishlist-brands.scss?inline';
import { Messages, WishlistBrandListItem } from '../../../@types/wishlist.js';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { brandListApi } from '../../../scripts/service/brand-list-api.js';
import { brandsContext } from '../../../scripts/context/wishlistContext.js';
import { BrandsState } from '../../../scripts/context/wishlist-state.js';
import heartIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_heart.svg?element';
import checkIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_check.svg?element';
import { hideIconFromAssistiveTechnology } from '../icon/icon-accessibility.js';
import '../wishlist-brand-remove-button/wishlist-brand-remove-button.js';
import '../wishlist-alert/wishlist-alert.js';

@customElement('ents-wishlist-brands')
export class WishlistBrandsElement extends LitElement {
  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({ context: i18nContext })
  private messages!: Messages;

  @consume({ context: brandsContext, subscribe: true })
  private brandsState!: BrandsState;

  @state() private loading: boolean = true;
  @state() private loadFailed: boolean = false;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  private async load() {
    // brands are cached in the app-level brandsState, so the API is only hit on the first tab visit
    if (this.brandsState.loaded) {
      this.loading = false;
      return;
    }
    this.loading = true;
    this.loadFailed = false;
    try {
      this.brandsState.updateAll(await brandListApi.listFollowed(this.wishlistAppConfig.brandsApiEndpoint));
    } catch (e) {
      console.error('failed to load followed brands', e);
      this.loadFailed = true;
    } finally {
      this.loading = false;
    }
  }

  private formatLogoAlt(brandName: string): string {
    const template = this.messages['ents_wishlist_brands_logo_alt'] ?? '{0}';
    return template.replace('{0}', brandName);
  }

  private get visibleBrands(): WishlistBrandListItem[] {
    return this.brandsState.getVisibleBrands();
  }

  protected updated() {
    // expose the loading state to assistive technology without needing a translated label
    this.setAttribute('aria-busy', String(this.loading));
  }

  render() {
    if (this.loading) return this.renderLoading();
    if (this.loadFailed) return this.renderError();
    if (this.visibleBrands.length === 0) return this.renderEmpty();
    return html`
      ${this.renderBenefitsHint()}
      <ul class="ents-wishlist-brands-grid" data-test-ents-wishlist-brands-grid>
        ${repeat(
          this.visibleBrands,
          (b) => b.code,
          (b) => this.renderTile(b),
        )}
        ${this.renderAzTile()}
      </ul>
    `;
  }

  private renderBenefitsHint() {
    return html`
      <wishlist-alert
        class="ents-wishlist-brands-hint"
        local-storage-id="ents-wishlist-brands-hint"
        data-test-ents-wishlist-brands-hint
      >
        <span slot="title" class="ents-wishlist-brands-hint__headline">
          ${this.messages['ents_wishlist_brands_hint_headline']}
        </span>
        <ul class="ents-wishlist-brands-hint__benefits">
          <li>${this.messages['ents_wishlist_brands_hint_benefit_1']}</li>
          <li>${this.messages['ents_wishlist_brands_hint_benefit_2']}</li>
          <li>${this.messages['ents_wishlist_brands_hint_benefit_3']}</li>
        </ul>
      </wishlist-alert>
    `;
  }

  private renderTile(brand: WishlistBrandListItem) {
    return html`
      <li class="ents-wishlist-brand-tile" data-test-ents-wishlist-brand-tile="${brand.code}">
        <a
          class="ents-wishlist-brand-tile__link"
          href="${this.wishlistAppConfig.brandsUrl}${brand.niceName}/"
          data-test-ents-wishlist-brand-tile-link="${brand.code}"
        >
          ${brand.logo
            ? html`<img
                class="ents-wishlist-brand-tile__logo"
                src="${brand.logo}"
                alt="${this.formatLogoAlt(brand.name)}"
                loading="lazy"
              />`
            : html`<span class="ents-wishlist-brand-tile__name">${brand.name}</span>`}
        </a>
        <ents-wishlist-brand-remove-button .brand="${brand}"></ents-wishlist-brand-remove-button>
      </li>
    `;
  }

  private renderAzTile() {
    return html`
      <li class="ents-wishlist-brand-tile ents-wishlist-brand-tile--link" data-test-ents-wishlist-brands-az-tile>
        <a href="${this.wishlistAppConfig.brandsUrl}" class="ents-wishlist-brand-tile__az-link bdk-link-primary">
          ${this.messages['ents_wishlist_brands_az_link_text']}
        </a>
      </li>
    `;
  }

  private renderEmpty() {
    const benefits = [
      this.messages['ents_wishlist_brands_empty_benefit_1'],
      this.messages['ents_wishlist_brands_empty_benefit_2'],
      this.messages['ents_wishlist_brands_empty_benefit_3'],
    ];
    return html`
      <div class="ents-wishlist-brands-empty" data-test-ents-wishlist-brands-empty>
        <div class="ents-wishlist-brands-empty__icon">${hideIconFromAssistiveTechnology(heartIcon())}</div>
        <div class="ents-wishlist-brands-empty__text">
          <h2 class="ents-wishlist-brands-empty__headline ents-copy-bold">
            ${this.messages['ents_wishlist_brands_empty_headline']}<br />${this.messages[
              'ents_wishlist_brands_empty_subheadline'
            ]}
          </h2>
          <ul class="ents-wishlist-brands-empty__benefits">
            ${benefits.map((benefit) => this.renderBenefit(benefit))}
          </ul>
        </div>
        <a class="bdk-button" type="button" variant="outline" size="medium" href="${this.wishlistAppConfig.brandsUrl}">
          ${this.messages['ents_wishlist_brands_az_link_text']}
        </a>
      </div>
    `;
  }

  private renderLoading() {
    return html`
      <div class="ents-wishlist-brands-loading" data-test-ents-wishlist-brands-loading>
        <span class="ents-wishlist-brands-loading__spinner" aria-hidden="true"></span>
      </div>
    `;
  }

  private renderError() {
    return html`
      <div class="ents-wishlist-brands-error" data-test-ents-wishlist-brands-error>
        <div class="ents-wishlist-brands-error__icon">${hideIconFromAssistiveTechnology(heartIcon())}</div>
        <p class="ents-wishlist-brands-error__text ents-copy">${this.messages['ents_wishlist_error_box_text']}</p>
      </div>
    `;
  }

  private renderBenefit(text?: string) {
    return html`
      <li class="ents-wishlist-brands-empty__benefit ents-copy">
        <span class="ents-wishlist-brands-empty__benefit-icon">${hideIconFromAssistiveTechnology(checkIcon())}</span>
        <span>${text}</span>
      </li>
    `;
  }
}
